import "./CreditKeeper.scss";
import Footer from "../../../../components/Footer/Footer";
import Header from "../../../../components/Header/Header";
import ProjectHero from "../../../../templates/ProjectHero/ProjectHero";
import ContentBlock from "../../../../templates/ContentBlock/ContentBlock";
import FullWidthImageSection from "../../../../templates/FullWidthImageSection/FullWidthImageSection";
import CardBlocksSection from "../../../../templates/CardBlocksSection/CardBlocksSection";
import ProjectPanelDisplay from "../../../../templates/ProjectPanelDisplay/ProjectPanelDisplay";
import { Helmet } from 'react-helmet';
import teamDataCards from "./team-data.json";
import researchInsightCards from "./research-data.json";

function CreditKeeper() {
  return (
    <div className="page-layout">
      <Helmet>
        <title>Credit Keeper - Sony Chen</title>
      </Helmet>

      <Header />
      <main className="page-layout__main">
        <section className="page-layout__project-wrapper">
            <ProjectHero
            tag="Credit score experience"
            header="Credit Keeper – Native Mobile Experience"
            body="Transforming Capital One Canada’s credit score journey from a web-based redirect into a fully native mobile experience focused on clarity, trust, and engagement."
            company="Capital One"
            role="Product Designer"
            year="2024"
            src="https://res.cloudinary.com/dhifyp04n/image/upload/v1779891283/Frame_2_urnycd.png"
            alt="A preview of the redesigned Credit Keeper credit score experience"
            />

            <ContentBlock
            isText
            isImage={true}
            isList={false}
            header="The problem"
            image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779984925/Top_dpcbpi.png"
            alt="A preview of the old Credit Keeper credit score experience"
            body={'Capital One Canada’s Credit Keeper feature allowed cardholders to check their credit score, but the experience relied on redirecting users to an external web page from within the app. This created a noticeable break in the user journey, moving users from a polished native experience into a slower and less cohesive browser flow.\n\nThe goal was to redesign Credit Keeper as a fully native mobile experience that felt seamlessly integrated within the app, provided richer access to credit insights, and established a stronger foundation for future financial wellness features.'}
            />

            <ContentBlock 
            isText={true}
            isImage={false}
            isList={false}
            header="How we divided the work"
            body="This was a collaborative project with Gianina Ramos, a fellow designer on the team. Alongside the feature work, we were also refining the mobile design system to align with evolving technical requirements, making clear ownership, thorough documentation, and strong cross-functional collaboration essential throughout the project."
            />

            <CardBlocksSection cards={teamDataCards} variant="flat" />

            <ContentBlock 
            isText={true}
            isImage={false}
            isList={false}
            header="What we learned"
            body="Gianina led user testing and workshop facilitation, with findings continuously informing both the UX strategy and UI decisions throughout the design process. Key themes that emerged:"
            />

            <CardBlocksSection cards={researchInsightCards} />


            <ContentBlock
            isText
            isImage={false}
            isList={false}
            header="What we built"
            alt="A preview of the old Credit Keeper credit score experience"
            body="Following workshops and research synthesis, the project scope was refined alongside the PM and PO. The redesign transformed Credit Keeper into a fully native experience focused on clarity, trust, and helping users better understand the factors impacting their credit score and how to improve it."
            />

            <FullWidthImageSection 
            image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779893339/Full_mpglqb.png"
            variant="image-only"
            alt="Credit Keeper redesign overview"
            />

            <ProjectPanelDisplay
            header="Native score display"
            body="A fully native credit score experience replacing the previous web redirect, designed to feel seamless within the Capital One app while improving performance and accessibility."
            image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779984415/Side_jlztj1.png"
            alt="A preview of the native score display"
            isReverse={false}
            />

            <ProjectPanelDisplay
            header="Score history chart"
            body="An interactive timeline showing how users’ credit scores changed over time, helping reinforce awareness and long-term financial habits."
            image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779894507/Side-1_lnhl7r.png"
            alt="A preview of the score history chart"
            isReverse={true}
            />

            <ProjectPanelDisplay
                header="Score factor breakdown"
                body="Clear, plain-language explanations highlighted the factors impacting a user’s credit score, making complex credit data easier to understand and act on."
                image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779894507/Side-2_xaluhd.png"
                alt="A preview of the score factor breakdown"
                isReverse={false}
            />

            <ProjectPanelDisplay
                header="Expanded credit insights"
                body="As user testing continued, users expressed interest in seeing more detailed insights into the factors affecting their credit score. Additional contextual information was introduced to provide greater transparency around score changes and overall credit health."
                image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779895295/Side-1_y72csm.png"
                alt="A preview of the detailed credit insights"
                isReverse={true}
            />

          <ProjectPanelDisplay
            header="Score change alerts"
            body="Push notifications and in-app alerts designed to proactively notify users of credit score changes, reducing the need for manual monitoring."
            image="https://res.cloudinary.com/dhifyp04n/image/upload/v1779894509/Side-3_m7lxdz.png"
            alt="A preview of the score change alerts"
            isReverse={false}
            />

            <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={false}
                        header="Outcome"
                        body={'Credit Keeper launched as a fully native feature across iOS and Android, replacing the previous browser redirect with a seamless in-app experience aligned with Capital One’s design system and mobile interaction patterns.\n\nThe redesign gave users richer visibility into their credit health through native access to score history, factor breakdowns, and proactive alerts, while also creating a stronger foundation for future financial wellness experiences within the app.'}
                    />
                        
                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={false}
                        header="Key takeaways "
                        bodies={[
                          {
                          subheader: "Clear ownership strengthens collaboration 🤝",
                          body: "Working alongside Gianina with clearly defined responsibilities allowed both of us to go deeper in our respective areas while maintaining strong collaboration throughout the project. Research findings continuously informed the UX and UI decisions I was designing."
                          },
                          {
                          subheader: "Native experiences build trust 📱",
                          body: "Users weren’t only frustrated by the usability of the web redirect. Leaving the native app experience also reduced confidence and trust. Rebuilding the feature natively helped create a more seamless and trustworthy experience."
                          },
                          {
                          subheader: "Context makes information actionable 💡",
                          body: "A credit score alone wasn’t enough for users to take meaningful action. Providing score history, factor breakdowns, and proactive alerts helped transform the experience from informational into something more understandable and actionable."
                          }
                      ]}
                    />

          </section>
        <Footer noBackgroundColor="true" />
        </main>
        </div>
  );
}

export default CreditKeeper;
